// src/pages/admin/Users.tsx
//
// User + role management. Two views:
//
//   - Read-only mirror of the Keycloak realm's users (in v1:
//     rendered from the demo seed since the local Keycloak's
//     admin REST API isn't reachable from the BENCHMARK app
//     over CORS; the production flow is admin-console-driven).
//
//   - Per-user role assignment (which BENCHMARK role each
//     Keycloak group claim maps to).
//
// Per docs/operations/iam-model.md §3 the mapping is:
//
//   bench-approvers  -> admin
//   bench-auditors   -> auditor
//   bench-marketing  -> marketing-sales
//   bench-sales      -> sales
//   bench-customer   -> customer
//   bench-staff only -> demo
//
// In v1 the mapping is fixed (encoded in src/lib/auth.ts). The
// future wp12a adds a per-tenant override.

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Users as UsersIcon,
  ShieldCheck,
  UserPlus,
  RotateCcw,
  Mail,
  KeyRound,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { EmptyState } from "@/components/ui/EmptyState";
import { cn } from "@/lib/cn";
import { useAuth } from "@/components/auth/AuthContext";
import { getAccountType, type AccountType } from "@/lib/auth";

interface DemoUser {
  id: string;
  username: string;
  email: string;
  fullName: string;
  groups: string[];
  accountType: AccountType;
}

// Seed users (mirror of what ops/scripts/keycloak-setup.sh
// provisions in the local-dev Keycloak realm). In production
// this list comes from /admin/realms/benchmark-dev/users.
const SEED_USERS: DemoUser[] = [
  {
    id: "u-admin",
    username: "admin",
    email: "admin@benchmark-av.be",
    fullName: "Admin Account",
    groups: ["bench-staff", "bench-approvers"],
    accountType: "admin",
  },
  {
    id: "u-demo",
    username: "demo",
    email: "demo@benchmark-av.be",
    fullName: "Demo Account",
    groups: ["bench-staff"],
    accountType: "demo",
  },
  {
    id: "u-auditor",
    username: "auditor",
    email: "auditor@benchmark-av.be",
    fullName: "Mireille Audit",
    groups: ["bench-staff", "bench-auditors"],
    accountType: "auditor",
  },
  {
    id: "u-marketing",
    username: "marketing",
    email: "marketing@benchmark-av.be",
    fullName: "Camille Marketing",
    groups: ["bench-staff", "bench-marketing"],
    accountType: "marketing-sales",
  },
  {
    id: "u-sales-1",
    username: "sales-1",
    email: "alice@benchmark-av.be",
    fullName: "Alice Andersen",
    groups: ["bench-staff", "bench-sales"],
    accountType: "sales",
  },
  {
    id: "u-sales-2",
    username: "sales-2",
    email: "bob@benchmark-av.be",
    fullName: "Bob Bertels",
    groups: ["bench-staff", "bench-sales"],
    accountType: "sales",
  },
];

const ROLE_LABEL_KEY: Record<AccountType, string> = {
  anonymous: "admin.users.roleAnonymous",
  demo: "admin.users.roleDemo",
  admin: "admin.users.roleAdmin",
  customer: "admin.users.roleCustomer",
  auditor: "admin.users.roleAuditor",
  "marketing-sales": "admin.users.roleMarketing",
  sales: "admin.users.roleSales",
  legal: "admin.users.roleLegal",
  management: "admin.users.roleManagement",
};

const ROLE_TONE: Record<AccountType, string> = {
  anonymous: "bg-fg-quaternary/15 text-fg-tertiary",
  demo: "bg-benchmark-teal/15 text-benchmark-teal-bright",
  admin: "bg-accent-green/15 text-accent-green",
  customer: "bg-accent-indigo/15 text-accent-indigo",
  legal: "bg-accent-info/15 text-accent-info",
  management: "bg-accent-info/15 text-accent-info",
  auditor: "bg-accent-amber/15 text-accent-amber",
  "marketing-sales": "bg-accent-indigo/15 text-accent-indigo",
  sales: "bg-accent-rose/15 text-accent-rose",
};

export function UsersPage() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [users, setUsers] = useState<DemoUser[]>(SEED_USERS);
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState<{
    username: string;
    email: string;
    fullName: string;
    groups: string[];
  }>({ username: "", email: "", fullName: "", groups: ["bench-staff"] });

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (!q) return true;
      return (
        u.username.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.fullName.toLowerCase().includes(q)
      );
    });
  }, [users, query]);

  // Sanity check: every seed user's accountType must match
  // getAccountType(groups). Otherwise the mapping code has
  // drifted; surface it as a banner.
  const mismatch = users.some(
    (u) => u.accountType !== getAccountType(u.groups),
  );

  function commit() {
    const next: DemoUser = {
      id: `u-${draft.username}`,
      username: draft.username,
      email: draft.email,
      fullName: draft.fullName,
      groups: draft.groups,
      accountType: getAccountType(draft.groups),
    };
    setUsers((s) => [...s, next]);
    setDraft({ username: "", email: "", fullName: "", groups: ["bench-staff"] });
    setShowCreate(false);
  }

  const isAdmin = auth.accountType === "admin";

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <UsersIcon
              className="h-5 w-5 text-benchmark-teal-bright"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
              {t("admin.users.title")}
            </h1>
            <Badge variant="default" size="sm">
              {users.length} {t("admin.users.accounts")}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-fg-tertiary">
            {t("admin.users.mirrorKeycloak")} <code>benchmark-dev</code>{" "}
            {t("admin.users.mirrorRealm")}{" "}
            <code>src/lib/auth.ts</code> {t("admin.users.mappingPer")}{" "}
            <code>docs/operations/iam-model.md</code> §3.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setUsers(SEED_USERS)}
          >
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("admin.users.resetToSeed")}
          </Button>
          {isAdmin && (
            <Button onClick={() => setShowCreate((s) => !s)}>
              <UserPlus className="h-4 w-4" strokeWidth={1.5} />
              {showCreate ? t("admin.users.cancel") : t("admin.users.newUser")}
            </Button>
          )}
        </div>
      </header>

      {mismatch && (
        <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-xs text-accent-rose">
          <ShieldCheck className="mr-2 inline h-3.5 w-3.5" strokeWidth={1.5} />
          {t("admin.users.driftStart")}{" "}
          <code>accountType</code>{" "}
          {t("admin.users.driftMismatch")}{" "}
          <code>getAccountType(groups)</code>{t("admin.users.driftVerify")}{" "}
          <code>src/lib/auth.ts</code>.
        </div>
      )}

      {showCreate && isAdmin && (
        <Card>
          <CardContent className="space-y-3 p-5">
            <h2 className="text-sm font-510 text-fg-primary">{t("admin.users.createUser")}</h2>
            <p className="text-xs text-fg-tertiary">
              {t("admin.users.createUserDescription")}
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-wider text-fg-tertiary">
                  {t("admin.users.username")}
                </label>
                <Input
                  value={draft.username}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, username: e.target.value }))
                  }
                  placeholder="sales-3"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-wider text-fg-tertiary">
                  {t("admin.users.email")}
                </label>
                <Input
                  value={draft.email}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, email: e.target.value }))
                  }
                  placeholder="charlie@benchmark-av.be"
                  type="email"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-wider text-fg-tertiary">
                  {t("admin.users.fullName")}
                </label>
                <Input
                  value={draft.fullName}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, fullName: e.target.value }))
                  }
                  placeholder="Charlie Chen"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-fg-tertiary">
                {t("admin.users.groups")}
              </label>
              <div className="mt-1 flex flex-wrap gap-2">
                {[
                  "bench-staff",
                  "bench-approvers",
                  "bench-auditors",
                  "bench-marketing",
                  "bench-sales",
                ].map((g) => {
                  const checked = draft.groups.includes(g);
                  return (
                    <button
                      key={g}
                      type="button"
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          groups: checked
                            ? d.groups.filter((x) => x !== g)
                            : [...d.groups, g],
                        }))
                      }
                      aria-pressed={checked}
                      className={cn(
                        "rounded-md border px-2 py-1 text-xs",
                        checked
                          ? "border-benchmark-teal/50 bg-benchmark-teal/10 text-fg-primary"
                          : "border-border bg-bg-surface text-fg-secondary hover:border-benchmark-teal/30",
                      )}
                    >
                      {g}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-[10px] text-fg-quaternary">
                {t("admin.users.groupHint")} <code>src/lib/auth.ts</code>.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
              <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
                {t("admin.users.cancel")}
              </Button>
              <Button
                size="sm"
                onClick={commit}
                disabled={
                  draft.username.trim().length < 2 ||
                  !draft.email.includes("@") ||
                  draft.fullName.trim().length < 2
                }
              >
                {t("admin.users.createUser")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4">
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("admin.users.searchPlaceholder")}
            aria-label={t("admin.users.searchAria")}
          />
        </CardContent>
      </Card>

      {visible.length === 0 ? (
        <EmptyState
          icon={<UsersIcon className="h-5 w-5 text-fg-quaternary" strokeWidth={1.5} />}
          title={t("admin.users.emptyTitle")}
          description={t("admin.users.emptyHint")}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {visible.map((u) => (
                <li key={u.id} className="grid items-center gap-2 px-5 py-3 sm:grid-cols-[1.5fr_1fr_1fr_auto]">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-510 text-fg-primary">
                        {u.fullName}
                      </span>
                      <span className="font-mono text-[10px] text-fg-quaternary">
                        @{u.username}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-1 text-[11px] text-fg-tertiary">
                      <Mail className="h-3 w-3" strokeWidth={1.5} />
                      <span>{u.email}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {u.groups.map((g) => (
                      <Badge key={g} variant="default" size="sm">
                        <KeyRound className="h-2.5 w-2.5" strokeWidth={2} />
                        {g}
                      </Badge>
                    ))}
                  </div>
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-510 uppercase tracking-wider",
                      ROLE_TONE[u.accountType],
                    )}
                  >
                    {t(ROLE_LABEL_KEY[u.accountType])}
                  </span>
                  <span className="text-[10px] text-fg-quaternary">
                    {u.groups.length} {u.groups.length === 1 ? t("admin.users.groupOne") : t("admin.users.groupOther")}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
